/**
 * 🎯 OperationsCenterPage — Mesa de Controle
 *
 * Decision-first operational cockpit:
 *   Dominant Status Block: global status + action buttons
 *   Panel 1: Critical Problems (col-span-2 when critical)
 *   Panel 2: Bottlenecks
 *   Panel 3: Overdue Actions
 *   Panel 4: Active Workflows
 *   Panel 5: Team Workload
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { useAppToast } from '../components/AppToast';
import type { OpsTimeRange, OpsSeverityFilter } from '../components/OpsCenterRibbon';
import {
  readOpsNav,
  useOperationsCenterRibbon,
  type CapacitySubView,
  type FlowSubView,
  type OpsScreen,
} from '../hooks/useOperationsCenterRibbon';
import { desktopRibbonInputClass, desktopRibbonSelectClass } from '../components/ribbon/DesktopRibbonControls';
import { useExecutionSelection } from '../contexts/ExecutionSelectionContext';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { useDataScope } from '../hooks/useDataScope';
import { OperationsCenterService } from '../services/operationsCenterService';
import type {
  OpsDashboardMetrics, OpsActiveWorkflow, OpsBottleneck,
  OpsCriticalProblem, OpsOverdueAction, OpsTeamMember, OpsAlert,
} from '../services/operationsCenterService';
import {
  AlertTriangle, ArrowRight, ChevronRight, Clock, Users, Target,
  Workflow, BarChart3, Shuffle, Zap, Search, ChevronDown, Check, Filter, X,
  Gauge, Building2, UserX,
} from 'lucide-react';
import { fetchDeptUnitsWithFallback, fetchExecutiveKpis, listOpenAndonActions } from '../services/executiveDeptUnitsService';
import {
  buildAndonOrigin,
  buildBottleneckOrigin,
  buildExecutionProblemOrigin,
  buildOverloadOrigin,
  openAndonCount,
  problemDraftPath,
} from '../components/ops/exceptionLoop';
import { OPS_ALL_UNITS } from '../components/ops/DeptUnitsMatrix';
import { OpsKaizenBenchmarkPanel } from '../components/ops/OpsKaizenBenchmarkPanel';
import { OpsBenchmarkOccupationPanel } from '../components/ops/benchmark/OpsBenchmarkOccupationPanel';
import { useTeamCapacityData } from '../components/ops/benchmark/useTeamCapacityData';
import { MAX_ACTIVE_TASKS_PER_OPERATOR } from '../components/ops/benchmark/teamCapacityShared';
import { OpsBenchmarkLoadPanel } from '../components/ops/benchmark/OpsBenchmarkLoadPanel';
import type { KaizenSubTab } from '../components/ops/kaizen/opsKaizenShared';
import { BENCHMARK_SHELL_FRAME, KAIZEN_SHELL_FRAME } from '../components/ops/kaizen/opsKaizenShared';
import { useAlertEngine } from '../hooks/useAlertEngine';
import { getHeatmapMatrix } from '../services/heatmapService';
import { OpsScreenHeader, OpsSimActiveBadge, OpsSimScopeNote, OpsSubnav } from '../components/ops/OpsPanelChrome';
import { describeOpsWindows } from '../components/ops/opsPeriodLabels';
import { OpsLiveFocusBlock } from '../components/ops/executive/OpsLiveFocusBlock';
import { OpsLiveKpiStrip } from '../components/ops/executive/OpsLiveKpiStrip';
import { OpsRecommendedActionCard } from '../components/ops/executive/OpsRecommendedActionCard';
import { OpsOperationalPrioritiesPanel } from '../components/ops/executive/OpsOperationalPrioritiesPanel';
import { OpsCapacityHeatmapMatrix } from '../components/ops/executive/OpsCapacityHeatmapMatrix';
import { OpsExecutiveDashboard } from '../components/ops/executive/OpsExecutiveDashboard';
import { useOpsKaizenData } from '../components/ops/kaizen/useOpsKaizenData';
import { teamOccupancy } from '../components/ops/executive/opsTeamMetrics';
import { getOccupancyTier } from '../components/ops/executive/opsExecutiveShared';
import { buildCapacityOutlook } from '../components/ops/capacityBalance';
import { localDateIso, resolvePlannedDemand } from '../components/ops/plannedDemand';
import { resolveCapacityParameters } from '../components/ops/capacityParameters';
import { listPlannedDemand } from '../services/plannedDemandService';
import { listCapacityParameters } from '../services/capacityParameterService';
import { calcularScoreOperacional } from '../services/heatmapService';
import type { CapacityConfig } from '../services/heatmapService';
import {
  runSimulation,
  SIM_ROLES,
  SIM_DEFAULT_FTE,
  SIM_SHIFT_MINUTES,
} from '../services/simulationEngine';
import { computeFloorKpis } from '../utils/opsFloorMetrics';

const ALL_UNITS = OPS_ALL_UNITS;

// ─── Styling Constants ──────────────────────────────────────

const PANEL_CLASS = 'rounded-sm border border-[var(--border-default)]/70 bg-[var(--bg-surface)] overflow-hidden text-[var(--text-primary)]';
const PANEL_HEADER_CLASS_BASE = 'flex items-center justify-between border-b border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/80';
const CARD_TITLE_CLASS = 'text-[10px] font-bold uppercase tracking-wider text-slate-800 dark:text-slate-100';
const PANEL_TITLE_CLASS = CARD_TITLE_CLASS;
const KPI_LABEL_CLASS = CARD_TITLE_CLASS;
const TABLE_HEAD_CLASS = 'text-[10px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200';
const PANEL_BODY_CLASS = '';
const LIVE_TOWER_PANEL_BODY = 'min-h-0 flex-1 overflow-y-auto divide-y divide-[var(--border-default)]/50';
const LIVE_COMPACT_PANEL_BODY = 'overflow-hidden divide-y divide-[var(--border-default)]/50';
const LIVE_ACTIONS_TABLE_BODY = 'min-h-0 flex-1 overflow-hidden';
const ACTIONS_TABLE_ROW =
  'grid w-full cursor-pointer grid-cols-[minmax(0,1fr)_40px_minmax(72px,0.25fr)_28px] items-center gap-1.5 border-b border-[var(--border-default)]/50 px-3 py-1 text-left text-[10px] font-normal text-[var(--text-primary)] last:border-b-0 hover:bg-[var(--button-hover)]/60 transition-colors duration-150';
const ROW_CLASS_BASE = 'flex items-center gap-2 border-b border-[var(--border-default)]/50 text-[10px] font-normal text-[var(--text-primary)] last:border-b-0 hover:bg-[var(--button-hover)]/60 transition-colors duration-150';
const BADGE_CLASS = 'inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-medium border border-[var(--border-default)]/60';
const ACTION_LINK_CLASS = 'flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors cursor-pointer';
const KPI_CARD_CLASS = 'flex flex-col gap-1 rounded-sm border border-[var(--border-default)]/70 bg-[var(--bg-surface)] px-4 py-2.5';
const KPI_OPS_CARD_CLASS = 'flex flex-col gap-0 rounded-sm border border-[var(--border-default)]/70 bg-[var(--bg-surface)] px-2.5 py-1';
const LIVE_CONTENT_GUTTER = 'px-4';
// density-aware class helpers (used where component has access to viewMode)
const rowClass = (compact: boolean) => `${ROW_CLASS_BASE} ${compact ? 'px-2 py-1' : 'px-4 py-2.5'}`;
const panelHeaderClass = (compact: boolean) => `${PANEL_HEADER_CLASS_BASE} ${compact ? 'px-2 py-1' : 'px-4 py-3'}`;

const LOAD_COLORS: Record<string, string> = {
  normal: 'bg-emerald-500',
  high: 'bg-amber-500',
  overload: 'bg-red-500',
};
const LOAD_LABELS: Record<string, string> = {
  normal: 'Normal',
  high: 'Alto',
  overload: 'Sobrecarga',
};
const LOAD_TEXT: Record<string, string> = {
  normal: 'text-emerald-600',
  high: 'text-amber-600',
  overload: 'text-red-600',
};

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'bg-red-500/15 text-red-500',
  high: 'bg-orange-500/15 text-orange-500',
  medium: 'bg-amber-500/15 text-amber-500',
  low: 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]',
};

// ─── Page ───────────────────────────────────────────────────

export const OperationsCenterPage: React.FC = () => {
  const organizationId = useOrganizationId();
  const { user } = useAuth();
  const userId = user?.id;
  const { roleType, profile: scopeProfile } = useDataScope();
  const deptInitializedRef = useRef(false);
  const queryClient = useQueryClient();
  const { showToast, showActionToast, ToastContainer } = useAppToast();
  const navigate = useNavigate();
  const { selectInstance, selectTask, selectProblem } = useExecutionSelection();

  const showAlerts = true;
  const viewMode = 'list' as const;
  const [timeRange, setTimeRange] = useState<OpsTimeRange>('shift');
  const [severityFilter, setSeverityFilter] = useState<OpsSeverityFilter>('all');
  const [searchText, setSearchText] = useState('');
  const [liveNow, setLiveNow] = useState(Date.now());
  const [opsScreen, setOpsScreen] = useState<OpsScreen>(() => readOpsNav(window.location.search).screen);
  const [capacitySubView, setCapacitySubView] = useState<CapacitySubView>(
    () => readOpsNav(window.location.search).capacity,
  );
  const [flowSubView, setFlowSubView] = useState<FlowSubView>(
    () => readOpsNav(window.location.search).flow,
  );

  const [deptFilter, setDeptFilter] = useState<string>(() => {
    const dept = new URLSearchParams(window.location.search).get('dept');
    return dept || ALL_UNITS;
  });
  const [focusStage, setFocusStage] = useState<string | null>(() => new URLSearchParams(window.location.search).get('stage'));
  const [focusWip, setFocusWip] = useState<number | null>(() => {
    const raw = new URLSearchParams(window.location.search).get('wip');
    const value = raw == null ? null : Number(raw);
    return value != null && Number.isFinite(value) ? value : null;
  });
  const [focusMinutes, setFocusMinutes] = useState<number | null>(() => {
    const raw = new URLSearchParams(window.location.search).get('minutes');
    const value = raw == null ? null : Number(raw);
    return value != null && Number.isFinite(value) ? value : null;
  });
  const [focusOperatorId, setFocusOperatorId] = useState<string | null>(() => new URLSearchParams(window.location.search).get('operator'));
  const [andonListOpen, setAndonListOpen] = useState(false);

  const heatmapShiftHours = 8;

  // ── Simulation Engine state ────────────────────────────────────────────
  const [simMode, setSimMode] = useState(false);
  const [simFteByRole, setSimFteByRole] = useState<Record<string, number>>(
    () => Object.fromEntries(SIM_ROLES.map(r => [r, SIM_DEFAULT_FTE[r]])),
  );
  // timeFactor slider: -50 to +50 (added to 100 → multiplier = 1 + val/100)
  const [simTimeSlider, setSimTimeSlider] = useState(0);
  // demandFactor slider: 50 to 200 (multiplier = val/100)
  const [simDemandSlider, setSimDemandSlider] = useState(100);
  /** Demanda do cenário em tarefas/h. Vazio usa a demanda planejada. */
  const [simDemandTasksPerHour, setSimDemandTasksPerHour] = useState<number | null>(null);
  /** FTE do cenário. Vazio usa o FTE persistido. Não grava. */
  const [simAvailableFte, setSimAvailableFte] = useState<number | null>(null);
  /** Produtividade do cenário. Nula reutiliza a produtividade configurada. */
  const [simTasksPerPersonHour, setSimTasksPerPersonHour] = useState<number | null>(null);

  const resetSimulation = useCallback(() => {
    setSimFteByRole(Object.fromEntries(SIM_ROLES.map(r => [r, SIM_DEFAULT_FTE[r]])));
    setSimTimeSlider(0);
    setSimDemandSlider(100);
    setSimDemandTasksPerHour(null);
    setSimTasksPerPersonHour(null);
    setSimAvailableFte(null);
  }, []);

  // ── Live clock (1s tick for real-time elapsed) ────────────
  useEffect(() => {
    const id = window.setInterval(() => setLiveNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.delete('tab');
    params.delete('view');
    if (opsScreen === 'capacity') {
      params.set('tab', 'capacity');
      params.set('view', capacitySubView);
    } else if (opsScreen === 'flow') {
      params.set('tab', 'flow');
      params.set('view', flowSubView);
    } else if (opsScreen === 'results') {
      params.set('tab', 'results');
    } else if (opsScreen === 'executive') {
      params.set('tab', 'executive');
    }
    if (opsScreen === 'flow' && flowSubView === 'bottlenecks' && focusStage) {
      params.set('stage', focusStage);
      if (focusWip != null) params.set('wip', String(focusWip));
      else params.delete('wip');
      if (focusMinutes != null) params.set('minutes', String(focusMinutes));
      else params.delete('minutes');
    } else {
      params.delete('stage');
      params.delete('wip');
      params.delete('minutes');
    }
    if (opsScreen === 'capacity' && capacitySubView === 'team' && focusOperatorId) {
      params.set('operator', focusOperatorId);
    } else {
      params.delete('operator');
    }
    const deptIdForUrl = deptFilter === ALL_UNITS || deptFilter.startsWith('synthetic-') ? null : deptFilter;
    if (deptIdForUrl) params.set('dept', deptIdForUrl);
    else params.delete('dept');
    const qs = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  }, [opsScreen, capacitySubView, flowSubView, focusStage, focusWip, focusMinutes, focusOperatorId, deptFilter]);

  // ── Queries (auto-refresh every 60s) ──────────────────────

  const STALE = 15_000;
  const REFETCH = 15_000;

  const deptFilterId =
    deptFilter === ALL_UNITS || deptFilter.startsWith('synthetic-') ? undefined : deptFilter;

  const minhaMesaQueuePath = useMemo(
    () => deptFilterId
      ? `/minha-mesa?dept_id=${encodeURIComponent(deptFilterId)}`
      : '/minha-mesa',
    [deptFilterId],
  );

  const { data: metrics } = useQuery<OpsDashboardMetrics>({
    queryKey: ['ops-metrics', organizationId, deptFilterId, userId],
    queryFn: () => OperationsCenterService.getDashboardMetrics(organizationId, deptFilterId, userId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId && !!userId,
  });

  const { data: firstUnassignedTaskId } = useQuery<string | null>({
    queryKey: ['ops-first-unassigned', organizationId],
    queryFn: () => OperationsCenterService.getFirstUnassignedTaskId(organizationId!),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId,
  });

  const { data: workflows = [] } = useQuery<OpsActiveWorkflow[]>({
    queryKey: ['ops-workflows', organizationId, deptFilterId],
    queryFn: () => OperationsCenterService.getActiveWorkflows(organizationId, deptFilterId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId,
  });

  const { data: bottlenecks = [] } = useQuery<OpsBottleneck[]>({
    queryKey: ['ops-bottlenecks', organizationId, deptFilterId],
    queryFn: () => OperationsCenterService.getBottlenecks(organizationId, deptFilterId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId,
  });

  const { data: problems = [] } = useQuery<OpsCriticalProblem[]>({
    queryKey: ['ops-problems', organizationId, deptFilterId],
    queryFn: () => OperationsCenterService.getCriticalProblems(organizationId, deptFilterId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId,
  });

  const { data: overdueActions = [] } = useQuery<OpsOverdueAction[]>({
    queryKey: ['ops-actions', organizationId, deptFilterId],
    queryFn: () => OperationsCenterService.getOverdueActions(organizationId, deptFilterId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId,
  });

  const { data: teamWorkload = [] } = useQuery<OpsTeamMember[]>({
    queryKey: ['ops-team', organizationId, deptFilterId],
    queryFn: () => OperationsCenterService.getTeamWorkload(organizationId, deptFilterId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId,
  });

  const { data: alerts = [] } = useQuery<OpsAlert[]>({
    queryKey: ['ops-alerts', organizationId],
    queryFn: () => OperationsCenterService.getAlerts(organizationId),
    staleTime: STALE,
    refetchInterval: REFETCH,
    enabled: !!organizationId && showAlerts,
  });

  // ── Unidades de valor (user_organizations + org_departments) ──
  const { data: deptQueryResult } = useQuery({
    queryKey: ['ops-dept-units', organizationId],
    queryFn: () => fetchDeptUnitsWithFallback(organizationId),
    staleTime: 120_000,
    enabled: !!organizationId,
  });

  const deptUnits = deptQueryResult?.units ?? [];
  const deptUnitsFallback = deptQueryResult?.isFallback ?? false;
  const loadingDeptUnits = !deptQueryResult && !!organizationId;

  const departments = useMemo(
    () => deptUnits.map((u) => ({
      id: u.department.id,
      name: u.department.name,
      color: u.department.color,
    })),
    [deptUnits],
  );

  const canFilterAllUnits = roleType === 'director';

  const visibleDepartments = useMemo(() => {
    if (canFilterAllUnits) return departments;
    const userDeptId = scopeProfile?.department_id;
    if (!userDeptId) return departments;
    const scoped = departments.filter((d) => d.id === userDeptId);
    return scoped.length > 0 ? scoped : departments;
  }, [departments, canFilterAllUnits, scopeProfile?.department_id]);

  useEffect(() => {
    if (deptInitializedRef.current || !scopeProfile) return;
    if (new URLSearchParams(window.location.search).get('dept')) {
      deptInitializedRef.current = true;
      return;
    }
    if (canFilterAllUnits) {
      deptInitializedRef.current = true;
      return;
    }
    if (scopeProfile.department_id) {
      setDeptFilter(scopeProfile.department_id);
    }
    deptInitializedRef.current = true;
  }, [canFilterAllUnits, scopeProfile]);

  const { data: executiveKpis, isLoading: loadingExecutiveKpis } = useQuery({
    queryKey: ['ops-executive-kpis', organizationId, deptFilterId],
    queryFn: () => fetchExecutiveKpis(organizationId, deptFilterId),
    staleTime: 60_000,
    enabled: !!organizationId,
  });

  const { data: andonActions = [], isLoading: loadingAndonActions } = useQuery({
    queryKey: ['ops-andon-actions', organizationId, deptFilterId],
    queryFn: () => listOpenAndonActions(organizationId, deptFilterId),
    staleTime: 30_000,
    enabled: !!organizationId && andonListOpen,
  });

  // ── Handlers ──────────────────────────────────────────────

  // ── Alert Engine ────────────────────────────────────────────
  const {
    alerts: engineAlerts,
    groups: alertGroups,
    topCritical: engineTopCritical,
    dismissAlert,
    resolveAlert,
    onAlertClick,
  } = useAlertEngine({
    workflows,
    bottlenecks,
    problems,
    overdueActions,
    silentMode: opsScreen !== 'live',
  });

  const secondaryAlertGroups = useMemo(() => {
    const primaryId = engineTopCritical?.id;
    return alertGroups.filter((g) => g.topAlert.id !== primaryId).slice(0, 6);
  }, [alertGroups, engineTopCritical?.id]);

  const selectedDeptName = useMemo(() => {
    if (deptFilter === ALL_UNITS) return null;
    return departments.find(d => d.id === deptFilter)?.name ?? null;
  }, [deptFilter, departments]);

  // ── Filtered data (severity ribbon — dept already applied in API) ───────
  const filteredWorkflows = useMemo(() => {
    if (severityFilter === 'critical') return workflows.filter(w => w.is_overdue || (w.sla_remaining_minutes != null && w.sla_remaining_minutes <= 0));
    if (severityFilter === 'warning') return workflows.filter(w => w.is_overdue || (w.sla_remaining_minutes != null && w.sla_remaining_minutes <= 30));
    return workflows;
  }, [workflows, severityFilter]);

  const filteredProblems = useMemo(() => {
    if (severityFilter === 'critical') return problems.filter(p => p.severity === 'critical');
    if (severityFilter === 'warning') return problems.filter(p => p.severity === 'critical' || p.severity === 'high');
    return problems;
  }, [problems, severityFilter]);

  const filteredBottlenecks = useMemo(() => {
    if (severityFilter === 'critical') return bottlenecks.filter(bn => bn.stuck_count * (bn.avg_minutes || 1) > 200);
    return bottlenecks;
  }, [bottlenecks, severityFilter]);

  // ── Ribbon ────────────────────────────────────────────────

  // ── Ribbon filters (portal dropdown pattern) ─────────────

  type OpsDd = 'time' | 'severity' | 'dept' | null;
  const [openDd, setOpenDd] = useState<OpsDd>(null);
  const timeBtnRef = useRef<HTMLButtonElement>(null);
  const sevBtnRef = useRef<HTMLButtonElement>(null);
  const deptBtnRef = useRef<HTMLButtonElement>(null);
  const [ddPos, setDdPos] = useState({ top: 0, left: 0 });

  const toggleDd = useCallback((which: OpsDd) => {
    if (!which) return;
    const ref = which === 'time' ? timeBtnRef : which === 'dept' ? deptBtnRef : sevBtnRef;
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setDdPos({ top: r.bottom + 4, left: r.left });
    }
    setOpenDd(prev => prev === which ? null : which);
  }, []);

  useEffect(() => {
    if (!openDd) return;
    const close = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-ops-dd]')) return;
      setOpenDd(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openDd]);

  const filterBtnClass = 'flex h-[36px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[11px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';
  const ddPanelClass = 'ribbon-dropdown-panel absolute z-[9999] min-w-[160px] overflow-hidden rounded-[5px] border border-[var(--border-default)] bg-[var(--bg-surface)] py-1 shadow-2xl';
  const ddItemClass = 'flex w-full items-center gap-2 px-3 py-[6px] text-left text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  const TIME_OPTIONS: { value: OpsTimeRange; label: string }[] = [
    { value: 'shift', label: 'Turno Atual' },
    { value: 'today', label: 'Hoje' },
    { value: 'last24h', label: 'Últimas 24h' },
  ];
  const SEVERITY_OPTIONS: { value: OpsSeverityFilter; label: string; color: string }[] = [
    { value: 'all', label: 'Todos', color: '#94a3b8' },
    { value: 'critical', label: 'Crítico', color: '#ef4444' },
    { value: 'warning', label: 'Alerta', color: '#f97316' },
    { value: 'info', label: 'Info', color: '#3b82f6' },
  ];

  const activeTimeLabel = TIME_OPTIONS.find(o => o.value === timeRange)?.label ?? 'Turno Atual';
  const activeSevOption = SEVERITY_OPTIONS.find(o => o.value === severityFilter);

  const opsFiltersWidget = useMemo(() => (
    <div className="flex items-center gap-1.5">
      {/* Período */}
      <button
        ref={timeBtnRef}
        data-ops-dd=""
        type="button"
        className={filterBtnClass}
        title="Ao Vivo, Equipe e carteira usam a situação atual. Heatmap e Carga seguem este filtro. Fluxo e Resultados usam janelas próprias (7, 14 ou 30 dias), indicadas em cada indicador."
        onClick={() => toggleDd('time')}
      >
        <Clock size={12} className="text-[var(--text-secondary)]" />
        <span>{activeTimeLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openDd === 'time' && createPortal(
        <div data-ops-dd="" className={ddPanelClass} style={{ position: 'fixed', top: ddPos.top, left: ddPos.left }}>
          {TIME_OPTIONS.map(o => (
            <button key={o.value} type="button" className={ddItemClass} onClick={() => { setTimeRange(o.value); setOpenDd(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0" />
              <span className="flex-1">{o.label}</span>
              <Check size={11} className={timeRange === o.value ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Unidade de Valor / Departamento */}
      <button ref={deptBtnRef} data-ops-dd="" type="button" className={filterBtnClass} onClick={() => toggleDd('dept')}>
        <Building2 size={12} className="text-[var(--text-secondary)]" />
        <span className="max-w-[120px] truncate">{selectedDeptName ?? 'Todas as Unidades'}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openDd === 'dept' && createPortal(
        <div data-ops-dd="" className={ddPanelClass} style={{ position: 'fixed', top: ddPos.top, left: ddPos.left, maxHeight: 320, overflowY: 'auto' }}>
          {canFilterAllUnits && (
            <button type="button" className={ddItemClass} onClick={() => { setDeptFilter(ALL_UNITS); setOpenDd(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--text-muted)]" />
              <span className="flex-1">Todas as Unidades</span>
              <Check size={11} className={deptFilter === ALL_UNITS ? 'opacity-100' : 'opacity-0'} />
            </button>
          )}
          {visibleDepartments.filter((d) => !d.id.startsWith('synthetic-')).map(d => (
            <button key={d.id} type="button" className={ddItemClass} onClick={() => { setDeptFilter(d.id); setOpenDd(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ backgroundColor: d.color || '#64748b' }} />
              <span className="flex-1 truncate">{d.name}</span>
              <Check size={11} className={deptFilter === d.id ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
          {departments.length === 0 && (
            <div className="px-3 py-2 text-[11px] text-[var(--text-muted)]">Nenhum departamento configurado</div>
          )}
        </div>,
        document.body
      )}

      {/* Severidade */}
      <button ref={sevBtnRef} data-ops-dd="" type="button" className={filterBtnClass} onClick={() => toggleDd('severity')}>
        <Filter size={12} className="text-[var(--text-secondary)]" />
        <span>{activeSevOption?.label ?? 'Todos'}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openDd === 'severity' && createPortal(
        <div data-ops-dd="" className={ddPanelClass} style={{ position: 'fixed', top: ddPos.top, left: ddPos.left }}>
          {SEVERITY_OPTIONS.map(o => (
            <button key={o.value} type="button" className={ddItemClass} onClick={() => { setSeverityFilter(o.value); setOpenDd(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ backgroundColor: o.color }} />
              <span className="flex-1">{o.label}</span>
              <Check size={11} className={severityFilter === o.value ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Search */}
      <div className="flex min-w-[140px] w-[200px] max-w-[220px] items-center">
        <div className="flex h-[36px] w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
          <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
          <input
            type="text"
            placeholder="Buscar alerta ou processo..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
          />
          {searchText && (
            <button onClick={() => setSearchText('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
              <X size={10} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      </div>
    </div>
  ), [timeRange, severityFilter, searchText, openDd, ddPos, activeTimeLabel, activeSevOption, toggleDd, deptFilter, visibleDepartments, selectedDeptName, canFilterAllUnits]);

  const opsSimulationToggle = useMemo(() => (
    <div className="flex h-[36px] shrink-0 items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2.5">
      <Gauge size={12} className={simMode ? 'text-violet-400' : 'text-[var(--text-secondary)]'} />
      <span className="text-[10px] font-medium text-[var(--text-secondary)] whitespace-nowrap">Simulação</span>
      <button
        type="button"
        role="switch"
        aria-checked={simMode}
        onClick={() => setSimMode(v => !v)}
        className={`relative h-[18px] w-[34px] shrink-0 rounded-full transition-colors duration-200 ${
          simMode ? 'bg-violet-500' : 'bg-[var(--border-default)]'
        }`}
      >
        <span
          className={`absolute top-[2px] left-[2px] h-[14px] w-[14px] rounded-full bg-white shadow-sm transition-transform duration-200 ${
            simMode ? 'translate-x-[16px]' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  ), [simMode]);

  const ribbonAfterGroups = useMemo(() => {
    if (!opsFiltersWidget && !opsSimulationToggle) return undefined;
    return (
      <div className="flex h-full min-h-[var(--shell-ribbon-height,46px)] items-center gap-2 shrink-0">
        {opsFiltersWidget}
        {opsSimulationToggle}
      </div>
    );
  }, [opsFiltersWidget, opsSimulationToggle]);

  const benchmarkDeptMemberIds = useMemo(() => {
    if (!deptFilterId) return undefined;
    const unit = deptUnits.find((u) => u.department.id === deptFilterId);
    return unit?.members.map((m) => m.user_id) ?? [];
  }, [deptFilterId, deptUnits]);

  const ribbonConfig = useOperationsCenterRibbon({
    opsScreen,
    onOpsScreenChange: setOpsScreen,
    afterGroups: ribbonAfterGroups,
  });

  // ── Global status (decision block) ────────────────
  const globalStatus = useMemo(() => {
    const crits = metrics?.critical_problems ?? 0;
    const overdue = metrics?.overdue_tasks ?? 0;
    const bottleneckCount = bottlenecks.length;
    const overload = metrics?.team_overload ?? 0;

    if (crits > 0)
      return { level: 'critical' as const, text: `${crits} problema${crits > 1 ? 's' : ''} crítico${crits > 1 ? 's' : ''} — atenção imediata` };
    if (overdue > 5 || bottleneckCount > 3 || overload > 2)
      return { level: 'warning' as const, text: `${overdue} atrasos · ${bottleneckCount} gargalos · ${overload} sobrecargas` };
    return { level: 'stable' as const, text: 'Operação estável' };
  }, [metrics, bottlenecks.length]);

  const hasCritical = (metrics?.critical_problems ?? 0) > 0;
  const hasWarnings = (metrics?.overdue_tasks ?? 0) > 0 || bottlenecks.length > 0 || (metrics?.actions_overdue ?? 0) > 0;

  // ── Intelligence: next best action ─────────────────────
  const nextAction = useMemo(() => {
    const topProblem = filteredProblems.find(p => p.severity === 'critical') ?? filteredProblems[0];
    if (topProblem) {
      return {
        label: topProblem.title,
        reason: topProblem.severity === 'critical' ? 'Problema crítico requer atenção' : 'Problema prioritário aberto',
        impactoEstimado: topProblem.severity === 'critical' ? 'Alto impacto operacional' : 'Impacto moderado na equipe',
        path: '/problemas',
        actionLabel: 'Ver problema',
        severity: 'critical' as const,
        entityType: 'problem' as const,
        entityId: topProblem.id,
        responsible: topProblem.assigned_to,
        status: topProblem.status,
        deadline: null as string | null,
      };
    }
    const topOverdue = [...overdueActions].sort((a, b) => b.days_overdue - a.days_overdue)[0];
    if (topOverdue) {
      return {
        label: topOverdue.title,
        reason: `${topOverdue.days_overdue}d em atraso`,
        impactoEstimado: `Estimativa: +${topOverdue.days_overdue}d de atraso acumulado`,
        path: '/acoes',
        actionLabel: 'Ver ação',
        severity: 'warning' as const,
        entityType: 'action' as const,
        entityId: topOverdue.id,
        responsible: topOverdue.assigned_name,
        status: topOverdue.status,
        deadline: topOverdue.due_date,
      };
    }
    return null;
  }, [filteredProblems, overdueActions]);

  const firstSlaBreachedWorkflow = useMemo(
    () => workflows.find(
      (w) => w.is_overdue || (w.sla_remaining_minutes != null && w.sla_remaining_minutes <= 0),
    ) ?? null,
    [workflows],
  );

  const openBottleneckFocus = useCallback((bottleneck: OpsBottleneck) => {
    setFocusStage(bottleneck.stage_name);
    setFocusWip(bottleneck.stuck_count);
    setFocusMinutes(bottleneck.avg_minutes);
    setOpsScreen('flow');
    setFlowSubView('bottlenecks');
  }, []);

  const openSlaPriorityTarget = useCallback(() => {
    if (firstSlaBreachedWorkflow) {
      selectInstance(firstSlaBreachedWorkflow.id);
      navigate(`/execucoes/instancias/${encodeURIComponent(firstSlaBreachedWorkflow.id)}`);
      return;
    }
    setOpsScreen('flow');
    setFlowSubView('overview');
  }, [firstSlaBreachedWorkflow, navigate, selectInstance]);

  // ── Intelligence: top execution to resume ──────────────
  const topExecution = useMemo(() => {
    const overdue = workflows.filter(w => w.is_overdue).sort((a, b) => (b.elapsed_minutes ?? 0) - (a.elapsed_minutes ?? 0));
    return overdue[0] ?? workflows[0] ?? null;
  }, [workflows]);

  // ── Intelligence: overloaded team member ───────────────
  const overloadedMember = useMemo(() => {
    return teamWorkload.find((member) => (
      getOccupancyTier((member.tasks_active / MAX_ACTIVE_TASKS_PER_OPERATOR) * 100) === 'critical'
    )) ?? null;
  }, [teamWorkload]);
  const hasAnyOverload = overloadedMember !== null;

  // ── Benchmark date range (derived from timeRange) ────────────
  const { benchmarkStartDate, benchmarkEndDate } = useMemo(() => {
    const now = new Date();
    const end = now.toISOString();
    let start: Date;
    if (timeRange === 'today') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (timeRange === 'last24h') {
      start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    } else {
      // 'shift' — rolling 30 days for a meaningful benchmark sample
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
    return { benchmarkStartDate: start.toISOString(), benchmarkEndDate: end };
  }, [timeRange]);

  const benchmarkPeriodDays = useMemo(() => {
    if (timeRange === 'today' || timeRange === 'last24h') return 1;
    return 30;
  }, [timeRange]);

  const benchmarkPeriodLabel = useMemo(() => {
    if (timeRange === 'today') return 'Hoje';
    if (timeRange === 'last24h') return 'Últimas 24h';
    return '30 dias';
  }, [timeRange]);

  const opsWindows = describeOpsWindows(timeRange);
  /** Janela real dos gráficos de fluxo (chartDays = max(período, 7)). */
  const flowWindowLabel = opsWindows.flowCharts;
  /** Série financeira usa max(período, 14). */
  const financialWindowLabel = opsWindows.financialSeries;

  const kaizenSubTab: KaizenSubTab = opsScreen === 'results'
    ? 'financial'
    : flowSubView === 'bottlenecks'
      ? 'losses'
      : 'value-flow';

  const kaizenExecutiveData = useOpsKaizenData({
    organizationId: organizationId ?? '',
    periodDays: benchmarkPeriodDays,
  });

  const projectsInDelay = useMemo(() => {
    const fromActions = new Set(
      overdueActions.filter((action) => action.project_id).map((action) => action.project_id as string),
    );
    return Math.max(executiveKpis?.projectsOverdue ?? 0, fromActions.size);
  }, [overdueActions, executiveKpis?.projectsOverdue]);

  const { members: capacityMembers } = useTeamCapacityData(organizationId ?? undefined, {
    departmentId: deptFilterId,
  });

  const heatmapTeam = useMemo((): OpsTeamMember[] => {
    const byId = new Map<string, OpsTeamMember>();
    for (const member of capacityMembers) {
      if (member.total_active <= 0 && member.tasks_overdue <= 0) continue;
      const loadTier = getOccupancyTier((member.total_active / MAX_ACTIVE_TASKS_PER_OPERATOR) * 100);
      byId.set(member.user_id, {
        user_id: member.user_id,
        user_name: member.user_name,
        tasks_active: member.total_active,
        tasks_overdue: member.tasks_overdue,
        load_level: loadTier === 'critical' ? 'overload' : loadTier === 'high' ? 'high' : 'normal',
      });
    }
    for (const member of teamWorkload) {
      if (!byId.has(member.user_id)) byId.set(member.user_id, member);
    }
    return [...byId.values()].sort((a, b) => b.tasks_active - a.tasks_active);
  }, [capacityMembers, teamWorkload]);

  const capacityAvailablePct = useMemo(() => {
    const active = capacityMembers.reduce((sum, member) => sum + member.total_active, 0);
    return teamOccupancy(active, capacityMembers.length).slackPct;
  }, [capacityMembers]);

  // ── KPIs reais (chão de fábrica: fila, SLA, risco por execução) ──
  const floorKpis = useMemo(
    () => computeFloorKpis(workflows, bottlenecks),
    [workflows, bottlenecks],
  );
  const efficiencyRate = floorKpis.efficiencyRate;
  const slaCompliance = floorKpis.slaCompliance;

  // ── Operational Score (composite metric) ─────────────────
  const operationalScore = useMemo(() => calcularScoreOperacional({
    slaRiskPercent: slaCompliance != null ? 100 - slaCompliance : (metrics?.sla_risk_percent ?? 0),
    bottleneckCount: bottlenecks.length,
    overloadCount: metrics?.team_overload ?? 0,
    criticalProblems: metrics?.critical_problems ?? 0,
  }), [metrics, bottlenecks.length, slaCompliance]);

  // ── Main loss insight ────────────────────────────────────
  const mainLoss = useMemo((): string | null => {
    if ((metrics?.critical_problems ?? 0) > 0) return `${metrics!.critical_problems} problema(s) crítico(s) aberto(s)`;
    if (floorKpis.slaBreachedCount > 0) {
      return `${floorKpis.slaBreachedCount} exec. com SLA estourado (fila > 24h ou prazo vencido)`;
    }
    if (bottlenecks.length > 2) return `${bottlenecks.length} gargalos acumulando filas`;
    if ((metrics?.team_overload ?? 0) > 0) return `${metrics!.team_overload} membro(s) em sobrecarga`;
    if ((metrics?.overdue_tasks ?? 0) > 3) return `${metrics!.overdue_tasks} tarefas atrasadas`;
    return null;
  }, [metrics, bottlenecks.length, floorKpis.slaBreachedCount]);

  const liveTowerMeta = useMemo(() => {
    const stalledWfs = workflows.filter(w => w.is_overdue || w.status === 'on_hold');
    const waitingCount = metrics?.waiting_tasks ?? 0;
    const waitAboveLimit = waitingCount > 5;
    const slaAtRisk = floorKpis.slaBreachedCount > 0 || (slaCompliance != null && slaCompliance < 95);
    const topCriticalProblem =
      filteredProblems.find(p => p.severity === 'critical') ?? filteredProblems[0] ?? null;
    const priorityCount = stalledWfs.length + (slaAtRisk ? 1 : 0);
    return { stalledWfs, waitingCount, waitAboveLimit, slaAtRisk, topCriticalProblem, priorityCount };
  }, [workflows, metrics, floorKpis, slaCompliance, filteredProblems]);

  const actionImpactMetrics = useMemo(() => {
    if (!nextAction) return null;

    const topProblem = liveTowerMeta.topCriticalProblem;
    const topOverdue = [...overdueActions].sort((a, b) => b.days_overdue - a.days_overdue)[0];

    const affectedSector =
      selectedDeptName
      ?? topProblem?.department
      ?? topOverdue?.department_name
      ?? topOverdue?.project_name
      ?? engineTopCritical?.stage_name
      ?? null;

    let recoveryPoints = 5;
    if ((metrics?.critical_problems ?? 0) > 0 && nextAction.severity === 'critical') {
      recoveryPoints = Math.min(25, operationalScore.breakdown.critProblemPenalty || 15);
    } else if (nextAction.path === '/acoes' && topOverdue) {
      recoveryPoints = Math.min(18, Math.max(4, Math.round(topOverdue.days_overdue * 0.4)));
    } else if (topExecution?.is_overdue || (topExecution?.sla_remaining_minutes != null && topExecution.sla_remaining_minutes <= 0)) {
      recoveryPoints = Math.min(15, Math.max(4, Math.round(operationalScore.breakdown.slaContrib * 0.12)));
    } else if (bottlenecks.length > 0) {
      recoveryPoints = Math.min(12, operationalScore.breakdown.bottleneckPenalty || 8);
    }

    let queueMinutes = 0;
    if (topExecution?.waiting_minutes != null && topExecution.waiting_minutes > 0) {
      queueMinutes = topExecution.waiting_minutes;
    } else if (topExecution?.sla_remaining_minutes != null && topExecution.sla_remaining_minutes < 0) {
      queueMinutes = Math.abs(topExecution.sla_remaining_minutes);
    } else if (topProblem?.created_at) {
      queueMinutes = Math.round((Date.now() - new Date(topProblem.created_at).getTime()) / 60000);
    } else if (topOverdue) {
      queueMinutes = topOverdue.days_overdue * 24 * 60;
    } else if (engineTopCritical?.created_at) {
      queueMinutes = Math.round((Date.now() - new Date(engineTopCritical.created_at).getTime()) / 60000);
    }

    return {
      affectedSector,
      recoveryPoints,
      queueMinutes,
      delayLabel: queueMinutes > 0 ? formatDuration(queueMinutes) : null,
    };
  }, [
    nextAction,
    liveTowerMeta.topCriticalProblem,
    overdueActions,
    selectedDeptName,
    engineTopCritical,
    topExecution,
    metrics,
    operationalScore,
    bottlenecks.length,
  ]);

  // ── Simulation result (pure calculation, no network) ─────────────────
  const simResult = useMemo(() => {
    if (!simMode) return null;
    return runSimulation(
      {
        fteByRole: simFteByRole,
        timeFactor: 1 + simTimeSlider / 100,
        demandFactor: simDemandSlider / 100,
        shiftMinutes: SIM_SHIFT_MINUTES,
      },
      {
        efficiencyRate: efficiencyRate ?? 70,
        slaCompliance: slaCompliance ?? 70,
        bottleneckCount: bottlenecks.length,
        avgLeadTimeMinutes: metrics?.avg_lead_time_minutes ?? 120,
        totalActiveTasks: Math.max(1, metrics?.active_workflows ?? 10),
      },
    );
  }, [simMode, simFteByRole, simTimeSlider, simDemandSlider, efficiencyRate, slaCompliance, bottlenecks.length, metrics]);

  const scopedDeptUnit = useMemo(
    () => (deptFilterId ? deptUnits.find((u) => u.department.id === deptFilterId) : null),
    [deptFilterId, deptUnits],
  );

  const heatmapScopeMemberCount = useMemo(() => {
    if (deptFilterId) return benchmarkDeptMemberIds?.length ?? scopedDeptUnit?.members.length ?? 0;
    return deptUnits.reduce((total, unit) => total + unit.members.length, 0);
  }, [deptFilterId, benchmarkDeptMemberIds, scopedDeptUnit, deptUnits]);

  const heatmapFilters = useMemo(() => ({
    organizationId: organizationId ?? '',
    startDate: benchmarkStartDate,
    endDate: benchmarkEndDate,
    assigneeIds: benchmarkDeptMemberIds,
    searchText: searchText.trim() || undefined,
    scopeLabel: selectedDeptName ?? 'Toda a organização',
    scopeMemberCount: heatmapScopeMemberCount,
  }), [
    organizationId,
    benchmarkStartDate,
    benchmarkEndDate,
    benchmarkDeptMemberIds,
    searchText,
    selectedDeptName,
    heatmapScopeMemberCount,
  ]);

  const { data: heatmapMatrixData, isLoading: loadingHeatmapMatrix } = useQuery({
    queryKey: ['heatmap-v2-exec', heatmapFilters],
    queryFn: () => getHeatmapMatrix(heatmapFilters),
    staleTime: STALE,
    enabled: !!organizationId && opsScreen === 'capacity' && capacitySubView === 'heatmap',
  });

  const plannedDemandQuery = useQuery({
    queryKey: ['planned-demand', organizationId],
    queryFn: () => listPlannedDemand(organizationId ?? ''),
    staleTime: STALE,
    enabled: !!organizationId && opsScreen === 'capacity' && (capacitySubView === 'load' || capacitySubView === 'heatmap'),
  });

  const capacityParameterQuery = useQuery({
    queryKey: ['capacity-parameter', organizationId],
    queryFn: () => listCapacityParameters(organizationId ?? ''),
    staleTime: STALE,
    enabled: !!organizationId && opsScreen === 'capacity' && (capacitySubView === 'load' || capacitySubView === 'heatmap'),
  });

  const resolvedCapacity = useMemo(() => {
    if (!capacityParameterQuery.isSuccess) return null;
    return resolveCapacityParameters(
      capacityParameterQuery.data,
      { departmentId: deptFilterId ?? null },
      localDateIso(),
    );
  }, [capacityParameterQuery.isSuccess, capacityParameterQuery.data, deptFilterId]);

  const peopleInScope = useMemo(() => {
    if (deptFilterId) return scopedDeptUnit ? scopedDeptUnit.members.length : null;
    if (deptUnits.length === 0) return null;
    return deptUnits.reduce((total, unit) => total + unit.members.length, 0);
  }, [deptFilterId, scopedDeptUnit, deptUnits]);

  const resolvedDemand = useMemo(() => {
    if (!plannedDemandQuery.isSuccess) return null;
    return resolvePlannedDemand(
      plannedDemandQuery.data,
      { departmentId: deptFilterId ?? null },
      localDateIso(),
    );
  }, [plannedDemandQuery.isSuccess, plannedDemandQuery.data, deptFilterId]);

  const realOutlook = useMemo(() => buildCapacityOutlook({
    demandTasksPerHour: plannedDemandQuery.isSuccess ? (resolvedDemand?.value ?? null) : null,
    demandOrigin: resolvedDemand?.source ?? null,
    availableFte: capacityParameterQuery.isSuccess ? (resolvedCapacity?.availableFte?.value ?? null) : null,
    tasksPerPersonHour: capacityParameterQuery.isSuccess ? (resolvedCapacity?.productivityTasksPerPersonHour?.value ?? null) : null,
    peopleInScope,
  }), [
    plannedDemandQuery.isSuccess,
    resolvedDemand,
    capacityParameterQuery.isSuccess,
    resolvedCapacity,
    peopleInScope,
  ]);

  const simulatedOutlook = useMemo(() => {
    if (!simMode) return null;
    return buildCapacityOutlook({
      demandTasksPerHour: simDemandTasksPerHour ?? (plannedDemandQuery.isSuccess ? (resolvedDemand?.value ?? null) : null),
      demandOrigin: simDemandTasksPerHour == null ? (resolvedDemand?.source ?? null) : null,
      availableFte: simAvailableFte ?? (capacityParameterQuery.isSuccess ? (resolvedCapacity?.availableFte?.value ?? null) : null),
      tasksPerPersonHour: simTasksPerPersonHour ?? (capacityParameterQuery.isSuccess ? (resolvedCapacity?.productivityTasksPerPersonHour?.value ?? null) : null),
      peopleInScope,
    });
  }, [
    simMode,
    simAvailableFte,
    simDemandTasksPerHour,
    simTasksPerPersonHour,
    plannedDemandQuery.isSuccess,
    resolvedDemand,
    capacityParameterQuery.isSuccess,
    resolvedCapacity,
    peopleInScope,
  ]);

  /** Cor do mapa só recebe capacidade quando FTE e produtividade foram informados. Contagem de pessoas não entra. */
  const benchmarkLoadCapacityConfig = useMemo((): CapacityConfig | undefined => {
    const shown = simMode && simulatedOutlook ? simulatedOutlook : realOutlook;
    if (
      shown.capacityTasksPerHour != null
      && shown.capacityTasksPerHour > 0
      && shown.availableFte != null
      && shown.tasksPerPersonHour != null
    ) {
      return {
        totalCapacityPerHour: shown.capacityTasksPerHour,
        shiftHours: simMode ? SIM_SHIFT_MINUTES / 60 : heatmapShiftHours,
        fte: shown.availableFte,
        tasksPerPersonHour: shown.tasksPerPersonHour,
        topRoleLabel: simMode ? 'Simulação' : 'Configuração persistida',
        departmentName: scopedDeptUnit?.department.name ?? selectedDeptName ?? undefined,
        memberCount: peopleInScope ?? undefined,
      };
    }
    return undefined;
  }, [
    simMode,
    simulatedOutlook,
    realOutlook,
    heatmapShiftHours,
    scopedDeptUnit,
    selectedDeptName,
    peopleInScope,
  ]);

  // ── Render ────────────────────────────────────────────────────

  return (
    <ModuleLayout ribbonConfig={ribbonConfig}>
      <div className="flex h-full w-full flex-col overflow-hidden bg-[var(--bg-app)]">

          {/* ── Simulation Panel (Ao Vivo only) ───────── */}
          {simMode && opsScreen === 'live' && (
            <div className={`shrink-0 ${LIVE_CONTENT_GUTTER} mb-2`}>
            <div className="rounded-[4px] border border-violet-500/30 bg-[var(--bg-surface)] px-4 py-3">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gauge size={12} className="text-violet-400" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-violet-400">Modo Simulação</span>
                  <OpsSimActiveBadge />
                  <span className="text-[9px] text-violet-300/80">Afeta este painel e a Carga · os indicadores reais do Ao Vivo não mudam · sem persistência</span>
                </div>
                <button
                  type="button"
                  onClick={resetSimulation}
                  className="text-[10px] font-medium text-[var(--text-muted)] hover:text-violet-400 transition-colors"
                >
                  Resetar simulação
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {/* FTE por cargo */}
                <div>
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">FTE por Cargo</span>
                  <div className="space-y-2">
                    {SIM_ROLES.map(role => (
                      <label key={role} className="flex items-center gap-2">
                        <span className="w-20 shrink-0 text-[10px] text-[var(--text-secondary)]">{role}</span>
                        <input
                          type="range" min={0.5} max={5} step={0.5}
                          value={simFteByRole[role] ?? 1}
                          onChange={e => setSimFteByRole(prev => ({ ...prev, [role]: Number(e.target.value) }))}
                          className="flex-1 accent-blue-500"
                        />
                        <span className="w-8 shrink-0 text-right text-[10px] font-semibold tabular-nums text-blue-400">
                          {(simFteByRole[role] ?? 1).toFixed(1)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Tempo de atividade */}
                <div>
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">Tempo de Atividade</span>
                  <label className="flex flex-col gap-1">
                    <input
                      type="range" min={-50} max={50} step={5}
                      value={simTimeSlider}
                      onChange={e => setSimTimeSlider(Number(e.target.value))}
                      className="accent-blue-500"
                    />
                    <div className="flex justify-between text-[9px] text-[var(--text-muted)]">
                      <span>−50%</span>
                      <span className={`font-semibold ${simTimeSlider > 0 ? 'text-amber-400' : simTimeSlider < 0 ? 'text-emerald-400' : 'text-[var(--text-muted)]'}`}>
                        {simTimeSlider > 0 ? `+${simTimeSlider}%` : simTimeSlider < 0 ? `${simTimeSlider}%` : 'Neutro'}
                      </span>
                      <span>+50%</span>
                    </div>
                  </label>
                  {simResult && (
                    <p className="mt-1.5 text-[9px] text-[var(--text-muted)]">
                      Lead time simulado: <span className="font-semibold text-violet-300">{simResult.simulatedLeadTime}m</span>
                    </p>
                  )}
                </div>

                {/* Demanda */}
                <div>
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">Fator de carga simulada</span>
                  <label className="flex flex-col gap-1">
                    <input
                      type="range" min={50} max={200} step={10}
                      value={simDemandSlider}
                      onChange={e => setSimDemandSlider(Number(e.target.value))}
                      className="accent-blue-500"
                    />
                    <div className="flex justify-between text-[9px] text-[var(--text-muted)]">
                      <span>50%</span>
                      <span className={`font-semibold ${simDemandSlider > 100 ? 'text-amber-400' : simDemandSlider < 100 ? 'text-emerald-400' : 'text-[var(--text-muted)]'}`}>
                        {simDemandSlider}%
                      </span>
                      <span>200%</span>
                    </div>
                  </label>
                  <p className="mt-1 text-[9px] text-[var(--text-muted)]">Multiplica a carga observada neste cenário. Não é demanda planejada. A demanda em tarefas/h fica na Carga.</p>
                  {simResult && simResult.bottleneckRoles.length > 0 && (
                    <p className="mt-1.5 text-[9px] font-semibold text-red-400">
                      Gargalos: {simResult.bottleneckRoles.join(', ')}
                    </p>
                  )}
                  {simResult && simResult.bottleneckRoles.length === 0 && (
                    <p className="mt-1.5 text-[9px] text-emerald-400">Sem gargalos simulados</p>
                  )}
                </div>
              </div>

              {/* Role utilization strip */}
              {simResult && (
                <div className="mt-3 flex gap-2 border-t border-violet-500/20 pt-3">
                  {simResult.byRole.map(r => (
                    <div key={r.role} className="flex-1 min-w-0">
                      <div className="mb-1 flex items-center justify-between gap-1">
                        <span className="truncate text-[9px] text-[var(--text-muted)]">{r.role}</span>
                        <span className={`text-[9px] font-semibold tabular-nums ${
                          r.level === 'bottleneck' ? 'text-red-400' : r.level === 'attention' ? 'text-amber-400' : 'text-emerald-400'
                        }`}>{Math.round(r.utilization * 100)}%</span>
                      </div>
                      <div className="h-1 overflow-hidden rounded-full bg-[var(--bg-app)]">
                        <div
                          className={`h-full rounded-full transition-all duration-200 ${
                            r.level === 'bottleneck' ? 'bg-red-500' : r.level === 'attention' ? 'bg-amber-400' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${Math.min(100, Math.round(r.utilization * 100))}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
          )}

        {/* ── Scrollable body ── */}
        <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-hidden">

        {/* ── Ao Vivo — gestão por exceção ── */}
        {opsScreen === 'live' && (
          <div className={`flex h-full min-h-0 flex-col gap-2 overflow-hidden ${LIVE_CONTENT_GUTTER} py-2`}>
            <OpsScreenHeader
              kicker="Ao Vivo"
              question="Onde preciso atuar agora?"
              detail="Exceção do momento. O filtro do ribbon não altera estes indicadores."
            />
            <OpsLiveFocusBlock
              problems={filteredProblems}
              bottlenecks={filteredBottlenecks}
              overdueActions={overdueActions}
              projectsOverdue={projectsInDelay}
              onNavigate={navigate}
              onBottleneckFocus={openBottleneckFocus}
            />

            <OpsLiveKpiStrip
              isLoading={loadingExecutiveKpis}
              andonOpen={openAndonCount({
                andonTriggered: executiveKpis?.andonTriggered,
                escalatedManager: executiveKpis?.escalatedManager,
                criticalDirector: executiveKpis?.criticalDirector,
              })}
              andonCritical={executiveKpis?.criticalDirector ?? 0}
              onAndonClick={() => setAndonListOpen((open) => !open)}
              onLoadClick={() => {
                setFocusOperatorId(overloadedMember?.user_id ?? null);
                setOpsScreen('capacity');
                setCapacitySubView('team');
              }}
              slaCompliance={slaCompliance}
              slaBreachedCount={floorKpis.slaBreachedCount}
              capacityAvailablePct={capacityAvailablePct}
              teamOverload={metrics?.team_overload ?? 0}
              completedCount={
                kaizenExecutiveData.metrics?.throughput_7d ?? kaizenExecutiveData.totalThroughput
              }
              completedPeriodLabel={
                kaizenExecutiveData.metrics?.throughput_7d != null
                  ? opsWindows.tasks7d
                  : flowWindowLabel
              }
              completedFollowsRibbon={false}
            />

            {andonListOpen && (
              <div className={`${PANEL_CLASS} shrink-0 px-3 py-2`}>
                <p className="text-[11px] font-semibold text-[var(--text-primary)]">
                  Andons abertos são ações com sinal de Andon ou escalação crítica.
                </p>
                {loadingAndonActions ? (
                  <p className="mt-1 text-[10px] text-[var(--text-muted)]">Carregando ocorrências…</p>
                ) : andonActions.length === 0 ? (
                  <p className="mt-1 text-[10px] text-[var(--text-muted)]">Nenhuma ocorrência aberta neste escopo.</p>
                ) : (
                  <ul className="mt-2 divide-y divide-[var(--border-default)]/50">
                    {andonActions.map((action) => (
                      <li key={action.id} className="flex flex-wrap items-center gap-2 py-1.5">
                        <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-[var(--text-primary)]">
                          {action.title}
                        </span>
                        <span className="text-[10px] text-[var(--text-muted)]">
                          {action.sla_status === 'critical_director'
                            ? 'Escalação crítica'
                            : action.sla_status === 'escalated_manager'
                              ? 'Escalado ao gerente'
                              : 'Andon'}
                        </span>
                        <button
                          type="button"
                          onClick={() => navigate(`/acoes?actionId=${encodeURIComponent(action.id)}`)}
                          className="text-[10px] font-semibold text-[var(--nav-active-bar)]"
                        >
                          Ver ação
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(problemDraftPath(buildAndonOrigin({
                            actionId: action.id,
                            actionTitle: action.title,
                            slaStatus: action.sla_status,
                          })))}
                          className="text-[10px] font-semibold text-[var(--text-secondary)]"
                        >
                          Registrar problema
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="grid min-h-0 shrink-0 grid-cols-1 gap-2 overflow-y-auto lg:grid-cols-2 lg:[&:not(:has(>:nth-child(2)))]:grid-cols-1">
              {nextAction && actionImpactMetrics && (
                <OpsRecommendedActionCard
                  title={
                    engineTopCritical?.titulo
                      ?? (nextAction.path.startsWith('/execucoes') && topExecution?.current_stage_name
                        ? `Resolver atraso — ${topExecution.current_stage_name}`
                        : nextAction.label)
                  }
                  nextAction={nextAction}
                  impactMetrics={actionImpactMetrics}
                  responsible={nextAction.responsible}
                  status={engineTopCritical?.status ?? nextAction.status}
                  deadline={nextAction.deadline}
                  primaryLabel={engineTopCritical ? 'Abrir tarefa' : 'Resolver agora'}
                  secondaryLabel="Ver contexto"
                  onPrimary={() => {
                    if (engineTopCritical?.task_id) {
                      onAlertClick(engineTopCritical);
                      navigate(`/minha-mesa?task_id=${encodeURIComponent(engineTopCritical.task_id)}`);
                      return;
                    }
                    if (engineTopCritical?.execution_id) {
                      onAlertClick(engineTopCritical);
                      selectInstance(engineTopCritical.execution_id);
                      navigate(`/execucoes/instancias/${encodeURIComponent(engineTopCritical.execution_id)}`);
                      return;
                    }
                    if (nextAction.entityType === 'problem') {
                      selectProblem(nextAction.entityId);
                      navigate(`/problemas?id=${encodeURIComponent(nextAction.entityId)}`);
                      return;
                    }
                    navigate(`/acoes?actionId=${encodeURIComponent(nextAction.entityId)}`);
                  }}
                  tertiaryLabel={engineTopCritical?.task_id || engineTopCritical?.execution_id ? 'Registrar problema' : undefined}
                  onTertiary={engineTopCritical?.task_id || engineTopCritical?.execution_id ? () => {
                    navigate(problemDraftPath(buildExecutionProblemOrigin({
                      executionId: engineTopCritical?.execution_id,
                      taskId: engineTopCritical?.task_id,
                      title: engineTopCritical?.titulo || 'execução',
                    })));
                  } : undefined}
                  onSecondary={() => {
                    if (nextAction.entityType === 'action') {
                      const action = overdueActions.find((a) => a.id === nextAction.entityId);
                      if (action?.project_id) {
                        navigate(`/projetos/${encodeURIComponent(action.project_id)}`);
                        return;
                      }
                    }
                    if (topExecution) {
                      selectInstance(topExecution.id);
                      navigate(`/execucoes/instancias/${encodeURIComponent(topExecution.id)}`);
                      return;
                    }
                    if (nextAction.entityType === 'problem') {
                      selectProblem(nextAction.entityId);
                      navigate(`/problemas?id=${encodeURIComponent(nextAction.entityId)}`);
                    }
                  }}
                />
              )}

              <OpsOperationalPrioritiesPanel
                stalledWorkflows={liveTowerMeta.stalledWfs}
                teamWorkload={teamWorkload}
                slaAtRisk={liveTowerMeta.slaAtRisk}
                slaBreachedCount={floorKpis.slaBreachedCount}
                slaCompliance={slaCompliance}
                onSlaClick={openSlaPriorityTarget}
                onWorkflowClick={(id) => {
                  selectInstance(id);
                  navigate(`/execucoes/instancias/${encodeURIComponent(id)}`);
                }}
              />
            </div>

            {liveTowerMeta.waitingCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  if (firstUnassignedTaskId) {
                    navigate(`/minha-mesa?task_id=${encodeURIComponent(firstUnassignedTaskId)}`);
                    return;
                  }
                  navigate(minhaMesaQueuePath);
                }}
                className={`${PANEL_CLASS} flex shrink-0 items-center gap-2 border-l-[3px] px-3 py-2 text-left transition-colors hover:bg-[var(--button-hover)]/40 ${
                  liveTowerMeta.waitAboveLimit ? 'border-l-amber-500 bg-amber-500/[0.04]' : 'border-l-blue-500 bg-blue-500/[0.04]'
                }`}
              >
                <Users size={14} className={liveTowerMeta.waitAboveLimit ? 'text-amber-600' : 'text-blue-600'} />
                <span className="text-[11px] font-semibold text-[var(--text-primary)]">
                  {liveTowerMeta.waitingCount} tarefa{liveTowerMeta.waitingCount > 1 ? 's' : ''} sem atribuição
                </span>
                <span className="ml-auto flex items-center gap-0.5 text-[10px] font-bold text-[var(--nav-active-bar)]">
                  Atribuir responsável <ChevronRight size={11} />
                </span>
              </button>
            )}
          </div>
        )}

        {/* ── Capacidade › Heatmap ── */}
        {opsScreen === 'capacity' && capacitySubView === 'heatmap' && organizationId && (
          <div className="flex h-full min-h-0 flex-col gap-3 px-5 pb-5 pt-3">
            <OpsScreenHeader
              kicker="Capacidade"
              question="Onde existe desequilíbrio entre demanda e capacidade?"
              detail={`Heatmap · contagem real de tarefas · ${opsWindows.heatmapLoad}. A ocupação % só existe quando há FTE e produtividade gravados.`}
            />
            <OpsSubnav<CapacitySubView>
              items={[
                { id: 'heatmap', label: 'Heatmap' },
                { id: 'team', label: 'Equipe' },
                { id: 'load', label: 'Carga' },
              ]}
              value={capacitySubView}
              onChange={(id) => setCapacitySubView(id)}
            />
            <OpsSimScopeNote active={simMode} affects />
            <div className="flex flex-col gap-2 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-[9px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">
                  {simMode && simulatedOutlook ? 'Capacidade do cenário' : 'Capacidade configurada'}
                </span>
                {(simMode && simulatedOutlook ? simulatedOutlook : realOutlook).capacityTasksPerHour != null ? (
                  <span className="text-[11px] font-semibold tabular-nums text-[var(--text-primary)]">
                    {(simMode && simulatedOutlook ? simulatedOutlook : realOutlook).availableFte?.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} FTE
                    × {(simMode && simulatedOutlook ? simulatedOutlook : realOutlook).tasksPerPersonHour?.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} tarefa/pessoa/h
                    = {(simMode && simulatedOutlook ? simulatedOutlook : realOutlook).capacityTasksPerHour?.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} tarefas/h
                  </span>
                ) : (
                  <span className="text-[11px] text-[var(--text-muted)]">Parâmetro não configurado. Ocupação = —</span>
                )}
                <button type="button" onClick={() => navigate('/org/capacidade')} className="text-[10px] font-semibold text-[var(--nav-active-bar)]">
                  Organização → Capacidade e Demanda
                </button>
              </div>
              <p className="text-[9px] text-[var(--text-muted)]">
                A contagem de tarefas é real. 3 FTE × 1 tarefa/pessoa/h é só uma referência visual antiga e não pinta esta matriz. 100% = atenção. Acima de 100% = sobrecarga, somente com capacidade gravada.
              </p>
            </div>
            <OpsCapacityHeatmapMatrix
              matrix={heatmapMatrixData}
              team={heatmapTeam}
              capacityPerHour={(simMode && simulatedOutlook ? simulatedOutlook : realOutlook).capacityTasksPerHour}
              configuredFte={(simMode && simulatedOutlook ? simulatedOutlook : realOutlook).availableFte ?? undefined}
              tasksPerPersonHour={(simMode && simulatedOutlook ? simulatedOutlook : realOutlook).tasksPerPersonHour ?? undefined}
              isLoading={loadingHeatmapMatrix}
              periodLabel={opsWindows.heatmapLoad}
              occupancyCaption={(simMode && simulatedOutlook ? simulatedOutlook : realOutlook).capacityTasksPerHour == null ? 'Parâmetro não configurado' : 'Ocupação = carga observada / capacidade configurada'}
            />
          </div>
        )}

        {/* ── Capacidade › Equipe (antiga Ocupação / Headcount) ── */}
        {opsScreen === 'capacity' && capacitySubView === 'team' && organizationId && (
          <div className={`flex min-h-0 flex-1 flex-col gap-2 overflow-hidden ${BENCHMARK_SHELL_FRAME}`}>
            <OpsScreenHeader
              kicker="Capacidade · Equipe"
              question="Quem está sobrecarregado, subutilizado ou em risco?"
              detail="Carga atual. Não segue o filtro de período do ribbon."
            />
            <OpsSubnav<CapacitySubView>
              items={[
                { id: 'heatmap', label: 'Heatmap' },
                { id: 'team', label: 'Equipe' },
                { id: 'load', label: 'Carga' },
              ]}
              value={capacitySubView}
              onChange={(id) => setCapacitySubView(id)}
            />
            <OpsSimScopeNote active={simMode} affects={false} />
            {focusOperatorId ? (
              <div className="shrink-0 rounded-sm border border-sky-500/40 bg-sky-500/[0.06] px-4 py-2">
                <p className="text-[12px] font-semibold text-[var(--text-primary)]">
                  Você chegou aqui para entender a carga
                  {overloadedMember?.user_id === focusOperatorId && overloadedMember.user_name
                    ? ` de ${overloadedMember.user_name}`
                    : ''}.
                </p>
                <p className="mt-0.5 text-[10px] text-[var(--text-secondary)]">
                  Veja ocupação, tarefas e atrasos antes de decidir. O problema não é criado sozinho.
                </p>
                <button
                  type="button"
                  onClick={() => navigate(problemDraftPath(buildOverloadOrigin({
                    userId: focusOperatorId,
                    userName: overloadedMember?.user_id === focusOperatorId ? overloadedMember.user_name : null,
                    departmentId: deptFilterId,
                    departmentName: deptFilter !== ALL_UNITS ? selectedDeptName : null,
                  })))}
                  className="mt-1 text-[11px] font-semibold text-[var(--nav-active-bar)]"
                >
                  Registrar problema
                </button>
              </div>
            ) : null}
            <OpsBenchmarkOccupationPanel
              organizationId={organizationId}
              units={deptUnits}
              isLoading={loadingDeptUnits}
              isFallback={deptUnitsFallback}
              departmentId={deptFilterId}
              periodLabel="Agora · carga atual"
              highlightUserId={focusOperatorId}
            />
          </div>
        )}

        {/* ── Capacidade › Carga ── */}
        {opsScreen === 'capacity' && capacitySubView === 'load' && organizationId && (
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden px-5 pt-3">
            <OpsScreenHeader
              kicker="Capacidade · Carga"
              question="Onde existe desequilíbrio entre demanda e capacidade?"
              detail={`Mapa de tarefas por dia e hora · ${opsWindows.heatmapLoad}.`}
            />
            <OpsSubnav<CapacitySubView>
              items={[
                { id: 'heatmap', label: 'Heatmap' },
                { id: 'team', label: 'Equipe' },
                { id: 'load', label: 'Carga' },
              ]}
              value={capacitySubView}
              onChange={(id) => setCapacitySubView(id)}
            />
            <OpsBenchmarkLoadPanel
              heatmapFilters={heatmapFilters}
              periodLabel={opsWindows.heatmapLoad}
              capacityConfig={benchmarkLoadCapacityConfig}
              simulationMultiplier={simMode && simDemandSlider !== 100 ? simDemandSlider / 100 : undefined}
              simulationActive={simMode}
              realOutlook={realOutlook}
              simulatedOutlook={simulatedOutlook}
              simulatedDemandTasksPerHour={simMode ? simDemandTasksPerHour : null}
              onSimulatedDemandChange={setSimDemandTasksPerHour}
              simulatedTasksPerPersonHour={simTasksPerPersonHour}
              onSimulatedProductivityChange={setSimTasksPerPersonHour}
              simulatedAvailableFte={simAvailableFte}
              onSimulatedFteChange={setSimAvailableFte}
              demandQueryFailed={plannedDemandQuery.isError}
              demandQueryPending={plannedDemandQuery.isPending}
              onConfigureDemand={() => navigate('/org/capacidade')}
            />
          </div>
        )}

        {/* ── Fluxo (visão geral + gargalos) ── */}
        {opsScreen === 'flow' && organizationId && (
          <div className={`flex min-h-0 flex-1 flex-col gap-2 overflow-hidden ${KAIZEN_SHELL_FRAME}`}>
            <OpsScreenHeader
              kicker="Fluxo"
              question={
                flowSubView === 'bottlenecks'
                  ? 'Qual gargalo explica o acúmulo?'
                  : 'Onde o trabalho está acumulando?'
              }
              detail={`Gráficos: ${flowWindowLabel}. O filtro do ribbon está em ${opsWindows.ribbonLabel} e não redefine esta janela.`}
            />
            <OpsSubnav<FlowSubView>
              items={[
                { id: 'overview', label: 'Visão Geral' },
                { id: 'bottlenecks', label: 'Gargalos' },
              ]}
              value={flowSubView}
              onChange={(id) => setFlowSubView(id)}
            />
            <OpsSimScopeNote active={simMode} affects={false} />
            <OpsKaizenBenchmarkPanel
              organizationId={organizationId}
              periodDays={benchmarkPeriodDays}
              periodLabel={flowWindowLabel}
              deptLabel={deptFilter !== ALL_UNITS ? selectedDeptName : null}
              subTab={kaizenSubTab}
              contextTitle="Fluxo"
              onOpenBottlenecks={() => setFlowSubView('bottlenecks')}
              focusStage={focusStage}
              focusWip={focusWip}
              focusMinutes={focusMinutes}
              onRegisterProblem={focusStage ? () => {
                navigate(problemDraftPath(buildBottleneckOrigin({
                  stageName: focusStage,
                  departmentId: deptFilterId,
                  departmentName: deptFilter !== ALL_UNITS ? selectedDeptName : null,
                  wip: focusWip,
                  avgMinutes: focusMinutes,
                  periodLabel: flowWindowLabel,
                })));
              } : undefined}
              onDismissFocus={() => {
                setFocusStage(null);
                setFocusWip(null);
                setFocusMinutes(null);
              }}
            />
          </div>
        )}

        {/* ── Resultados (antigo Análise › Resultado) ── */}
        {opsScreen === 'results' && organizationId && (
          <div className={`flex min-h-0 flex-1 flex-col gap-2 overflow-hidden ${KAIZEN_SHELL_FRAME}`}>
            <OpsScreenHeader
              kicker="Resultados"
              question="Quanto valor as melhorias estão gerando?"
              detail="Benefício anual estimado dos Kaizens em monitoramento ou sustentação. Potencial e regressão ficam fora dessa soma. Não é caixa realizado."
            />
            <OpsSimScopeNote active={simMode} affects={false} />
            <OpsKaizenBenchmarkPanel
              organizationId={organizationId}
              periodDays={benchmarkPeriodDays}
              periodLabel={financialWindowLabel}
              deptLabel={deptFilter !== ALL_UNITS ? selectedDeptName : null}
              subTab="financial"
              contextTitle="Resultados"
            />
          </div>
        )}

        {opsScreen === 'executive' && organizationId && (
          <div className="flex h-full min-h-0 flex-col">
            <div className="shrink-0 px-6 pt-4">
              <OpsSimScopeNote active={simMode} affects={false} />
            </div>
          <OpsExecutiveDashboard
            systemStatus={kaizenExecutiveData.systemStatus}
            operationalKpis={kaizenExecutiveData.operationalKpis}
            slaGlobal={kaizenExecutiveData.metrics?.sla_rate_7d ?? slaCompliance ?? 0}
            slaLabel={
              kaizenExecutiveData.metrics?.sla_rate_7d != null
                ? 'SLA concluído · 7 dias'
                : 'SLA da fila ativa'
            }
            slaPeriodLabel={
              kaizenExecutiveData.metrics?.sla_rate_7d != null
                ? 'Tarefas concluídas no prazo'
                : 'Execuções abertas agora'
            }
            primaryBottleneck={kaizenExecutiveData.primaryBottleneck}
            projectsOverdue={projectsInDelay}
            capacityAvailablePct={capacityAvailablePct}
            throughput={kaizenExecutiveData.metrics?.throughput_7d ?? kaizenExecutiveData.totalThroughput}
            throughputPeriodLabel={
              kaizenExecutiveData.metrics?.throughput_7d != null
                ? 'Últimos 7 dias'
                : flowWindowLabel
            }
            teamOverdueCount={kaizenExecutiveData.teamOverdueCount}
            teamPendingCount={kaizenExecutiveData.teamPendingCount}
            teamInProgressCount={kaizenExecutiveData.teamInProgressCount}
            periodLabel={opsWindows.ribbonLabel}
            trends={kaizenExecutiveData.performanceTrends}
            estimatedImpact={
              kaizenExecutiveData.benefitsReady
                ? kaizenExecutiveData.registeredAnnualSavings
                : null
            }
            onOpenResults={() => setOpsScreen('results')}
          />
          </div>
        )}

        </div>{/* end scrollable body */}
      </div>

      <ToastContainer />
    </ModuleLayout>
  );
};

// ─── Sub-components ─────────────────────────────────────────

function formatActionSector(
  department: string | null | undefined,
  project: string | null | undefined,
): string {
  const value = (department || project || '').trim();
  if (!value || value === '—' || value === '-') return '';
  return value;
}

function ActionImpactMetric({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[var(--border-default)]/40 py-2 last:border-b-0 last:pb-0">
      <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-200">{label}</span>
      <span className={`truncate text-right text-[11px] font-semibold tabular-nums ${accent ?? 'text-[var(--text-primary)]'}`}>
        {value}
      </span>
    </div>
  );
}

function personInitials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return label.slice(0, 2).toUpperCase();
}

function OpsDiscreteAvatar({ name }: { name: string | null }) {
  if (!name?.trim()) {
    return (
      <span
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center text-[var(--text-muted)]/45"
        title="Usuário não atribuído"
      >
        <UserX size={14} strokeWidth={1.5} />
      </span>
    );
  }
  const label = name.trim();
  return (
    <span
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-500/20 text-[9px] font-bold text-[var(--text-secondary)]"
      title={label}
    >
      {personInitials(label)}
    </span>
  );
}

function EmptyPanel({ label, cta, onClick, stable, compact }: { label: string; cta?: string; onClick?: () => void; stable?: boolean; compact?: boolean }) {
  const isStable = stable || label.startsWith('✔');
  const isWarn = !isStable && label.startsWith('⚠');
  return (
    <div className={`flex flex-col items-center justify-center gap-1 ${compact ? 'py-0.5' : 'py-3 gap-1.5'}`}>
      <span className={`text-[10px] ${
        isStable ? 'text-emerald-500 font-medium'
        : isWarn  ? 'text-amber-500'
        : 'text-[var(--text-secondary)]'
      }`}>{label}</span>
      {cta && onClick && (
        <button
          type="button"
          onClick={onClick}
          className="text-[10px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          {cta} →
        </button>
      )}
    </div>
  );
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours >= 48) {
    const days = hours / 24;
    return days >= 10 ? `${Math.round(days)}d` : `${days.toFixed(1)}d`;
  }
  const wholeHours = Math.floor(hours);
  const mins = Math.round(minutes % 60);
  return mins > 0 ? `${wholeHours}h ${mins}m` : `${wholeHours}h`;
}

function formatTimeOpen(createdAt: string): string {
  const diffMin = Math.round((Date.now() - new Date(createdAt).getTime()) / 60000);
  if (diffMin < 60) return `${diffMin}m`;
  const hours = Math.floor(diffMin / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
